import { useMemo } from 'react';
import { useOrgContext } from '../components/org/OrgProvider';
import { useLanguage } from '../i18n/LanguageProvider';
import type { NavOption } from '../hooks/useNavigationOptions';
import { getSidecarSidebarEntryMetadata } from './tiles';

/**
 * Returns the list of `NavOption` rows for every sidecar enabled on
 * the active org under `appId` (e.g. `"rakine"`) that has a
 * registered sidebar-entry metadata entry.
 *
 * Sidebar twin of `useAppSidecarTiles`. Same data flow:
 *   - `orgSidecars` via `useOrgContext` — the org's enabled sidecars
 *     (already filtered to `enabled = true` server-side; see
 *     `getOrgSidecars` in `server/src/queries/orgSidecars.ts`).
 *   - `messages` via `useLanguage` — for sidecar-owned i18n keys.
 *   - `SIDECAR_SIDEBAR_ENTRY_REGISTRY` — display metadata each sidecar
 *     declares in its own `surfaces.tsx`.
 *
 * The parent app's `useNavigationOptions` composes this with its core
 * nav entries (overview, drivers, etc.) — sidecar entries are purely
 * additive. When no sidecars match, the returned array is empty so
 * the parent's `[...base, ...sidecar]` spread is a no-op and the
 * sidebar layout collapses naturally — i.e. apps without any sidecars
 * pay zero render cost.
 *
 * `route` is composed with the active org's id to mirror the core
 * nav entries' shape (the sidebar's `handleClick` prepends `/<app>`
 * so we don't double-prefix here).
 *
 * Returns `[]` (not `null`) so callers can spread without a guard.
 */
export const useAppSidecarSidebarEntries = (appId: string): NavOption[] => {
  const { org, orgSidecars, orgSidecarsLoaded } = useOrgContext();
  const { messages } = useLanguage();

  return useMemo(() => {
    if (!orgSidecarsLoaded || !org) return [];
    return (
      orgSidecars
        .filter(s => s.appId === appId && s.enabled)
        // Stable order: alphabetical by sidecarKey. Sidecars are
        // appended AFTER core entries by the parent; ordering them
        // deterministically here keeps multi-sidecar layouts
        // predictable across reloads. Within a single sidecar with
        // multiple surfaces (e.g. solarbox's customers + bike-control),
        // the order comes from the registry array — flatMap below
        // preserves it.
        .slice()
        .sort((a, b) => a.sidecarKey.localeCompare(b.sidecarKey))
        .flatMap(s =>
          getSidecarSidebarEntryMetadata(appId, s.sidecarKey).map(meta => {
            const entry: NavOption = {
              label: meta.label(messages),
              value: meta.value,
              route: `/orgs/${org.id}${meta.routePath}`,
              icon: meta.iconName,
            };
            return entry;
          })
        )
    );
  }, [appId, orgSidecars, orgSidecarsLoaded, org, messages]);
};

interface AppSidecarSidebarEntriesProps {
  /** App identifier the parent is rendering nav entries for, e.g.
   * `"rakine"`. */
  appId: string;
  /** Render-prop the parent uses to receive the sidecar entry list.
   * The parent typically spreads it next to its own core entries, so
   * giving it back as data (rather than rendering here) keeps a
   * single sidebar in charge of the visual shell. */
  children: (entries: NavOption[]) => JSX.Element | null;
}

/**
 * Component wrapper around `useAppSidecarSidebarEntries` for callers
 * that want a JSX-shaped API. `useNavigationOptions` uses the hook
 * directly; this exists for parity with `<AppSidecarTiles>` and for
 * future call sites where a component reads more naturally.
 */
export const AppSidecarSidebarEntries = ({
  appId,
  children,
}: AppSidecarSidebarEntriesProps): JSX.Element | null => {
  const entries = useAppSidecarSidebarEntries(appId);
  return children(entries);
};
